/**
 * Admin roles: named bundles of permissions, defined in code.
 *
 * The `UserRole` table stores which user holds which role by its key here. A
 * new role is one entry in `ROLES`. Roles live in code, not in a table,
 * because a role edited in the database could name permissions nothing checks.
 *
 * Aliases: roles, admin roles, role definitions, user roles
 */

import { z } from "zod";

import { ALL_PERMISSIONS, type RoleDefinition } from "~/lib/shared/permissions";

export const ROLES = {
  admin: {
    label: "Admin",
    description: "Every admin page and action, including users and roles.",
    opens: "every admin page and action",
    permissions: ALL_PERMISSIONS,
  },
  // Reviews and edits courses. `videos:read` lets a week pick a library
  // video; the library itself and Placements stay out of reach.
  course_reviewer: {
    label: "Course reviewer",
    description: "Courses (view and edit) and the video library (view only).",
    opens: "Courses and the video library",
    permissions: ["courses:write", "videos:read"],
  },
} as const satisfies Record<string, RoleDefinition>;

export type RoleKey = keyof typeof ROLES;

/** True when `key` names a role code defines. */
export function isRoleKey(key: string): key is RoleKey {
  return Object.hasOwn(ROLES, key);
}

/**
 * Every role code defines, in definition order. `ROLES` is a non-empty
 * literal, so the cast to a non-empty tuple only restates what is known; it
 * lets `z.enum` list the roles in JSON schemas (the admin MCP tools).
 */
export const ROLE_KEYS = Object.keys(ROLES) as [RoleKey, ...RoleKey[]];

/** A role key code defines; anything else fails validation. */
export const roleKeySchema = z.enum(ROLE_KEYS);

/**
 * The label for a stored role key. A key code no longer defines shows as
 * itself, so nobody holds access that a screen hides.
 */
export function roleLabel(key: string): string {
  return isRoleKey(key) ? ROLES[key].label : key;
}

/**
 * Stored role keys in display order: defined roles in definition order, then
 * keys code no longer defines, alphabetically.
 */
export function sortRoleKeys(keys: readonly string[]): string[] {
  const rank = (key: string): number =>
    isRoleKey(key) ? ROLE_KEYS.indexOf(key) : ROLE_KEYS.length;
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}
